import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { DiscoveryMembership } from '@app/types/auth';

type LocalConnection = 'passkey' | 'password';

interface DiscoveryState {
  status: 'idle' | 'verified';
  email?: string;
  emailTx?: string;
  emailTxExpiresAt?: string | null;
  localConnections: LocalConnection[];
  memberships: DiscoveryMembership[];
}

export interface DiscoveryResult {
  email: string;
  emailTx: string;
  emailTxExpiresAt?: string | null;
  localConnections?: LocalConnection[];
  memberships: DiscoveryMembership[];
}

interface AuthFlowContextValue {
  discovery: DiscoveryState;
  selectedMembership?: DiscoveryMembership;
  setDiscoveryResult: (result: DiscoveryResult) => void;
  clearDiscovery: () => void;
  selectMembership: (membership: DiscoveryMembership | undefined) => void;
}

const initialDiscoveryState: DiscoveryState = {
  status: 'idle',
  localConnections: [],
  memberships: []
};

const AuthFlowContext = createContext<AuthFlowContextValue | undefined>(undefined);

export interface AuthFlowProviderProps {
  children: ReactNode;
}

export const AuthFlowProvider = ({ children }: AuthFlowProviderProps) => {
  const [discovery, setDiscovery] = useState<DiscoveryState>(initialDiscoveryState);
  const [selectedMembership, setSelectedMembership] = useState<DiscoveryMembership | undefined>();

  const setDiscoveryResult = useCallback((result: DiscoveryResult) => {
    setDiscovery({
      status: 'verified',
      email: result.email,
      emailTx: result.emailTx,
      emailTxExpiresAt: result.emailTxExpiresAt ?? null,
      localConnections: result.localConnections ?? [],
      memberships: result.memberships
    });

    if (result.memberships.length === 1) {
      setSelectedMembership(result.memberships[0]);
    } else {
      setSelectedMembership(undefined);
    }
  }, []);

  const clearDiscovery = useCallback(() => {
    setDiscovery(initialDiscoveryState);
    setSelectedMembership(undefined);
  }, []);

  const selectMembership = useCallback((membership: DiscoveryMembership | undefined) => {
    setSelectedMembership(membership);
  }, []);

  const value = useMemo<AuthFlowContextValue>(
    () => ({
      discovery,
      selectedMembership,
      setDiscoveryResult,
      clearDiscovery,
      selectMembership
    }),
    [clearDiscovery, discovery, selectMembership, selectedMembership, setDiscoveryResult]
  );

  return <AuthFlowContext.Provider value={value}>{children}</AuthFlowContext.Provider>;
};

export const useAuthFlow = (): AuthFlowContextValue => {
  const ctx = useContext(AuthFlowContext);
  if (!ctx) {
    throw new Error('useAuthFlow must be used within an AuthFlowProvider');
  }
  return ctx;
};
